// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import type { ClientCommand } from "./types";

// parseInput turns a composer line into a homeserver client command, supporting
// the IRC-style slash commands. A non-command line becomes a plaintext message.
//
// Returns null with an `error` when the command is malformed, or `{ command }`
// to send. `plaintext` (for non-encrypted fallback / display) is also returned.
export interface ParseResult {
  command?: ClientCommand;
  plaintext?: string; // body to encrypt/display for "send" of a message
  error?: string;
  help?: boolean;
  list?: boolean; // open the channel directory
  whois?: string; // show info about this user ID
  dm?: string; // open a 1:1 direct message with this user ID (/msg, /query)
  membership?: { displayname?: string; status_msg?: string }; // /nick, /away, /back
  slowmode?: number; // set channel slow-mode interval (seconds)
  poll?: { question: string; answers: string[] }; // /poll
}

export const COMMANDS = [
  "/me <action>",
  "/topic <text>",
  "/join <roomID>",
  "/knock <roomID>",
  "/invite <user>",
  "/kick <user> [reason]",
  "/ban <user> [reason]",
  "/joinrule <public|invite|knock|restricted>",
  "/nick <display name>",
  "/away [reason]",
  "/back",
  "/slowmode <seconds>",
  "/poll <question> | <option> | <option> …",
  "/list",
  "/whois <user>",
  "/msg <user>",
  "/help",
];

export function parseInput(line: string, roomID: string): ParseResult {
  const text = line.trim();
  if (text === "") return {};
  if (!text.startsWith("/")) {
    return {
      plaintext: text,
      command: { action: "send", room_id: roomID, type: "m.room.message", content: { msgtype: "m.text", body: text } },
    };
  }

  const [cmd, ...rest] = text.slice(1).split(/\s+/);
  const arg = rest.join(" ");

  switch (cmd.toLowerCase()) {
    case "help":
      return { help: true };

    case "list":
      return { list: true };

    case "whois": {
      if (!arg) return { error: "/whois needs a user (nick or @user:server)" };
      const server = roomID.split(":")[1] || "zalmoxis.local";
      return { whois: arg.startsWith("@") ? arg : `@${arg}:${server}` };
    }

    case "msg":
    case "query": {
      const who = rest[0] ?? "";
      if (!who) return { error: "/msg needs a user (nick or @user:server)" };
      const server = roomID.split(":")[1] || "zalmoxis.local";
      return { dm: who.startsWith("@") ? who : `@${who}:${server}` };
    }

    case "me":
      if (!arg) return { error: "/me needs an action" };
      return {
        plaintext: arg,
        command: { action: "send", room_id: roomID, type: "m.room.message", content: { msgtype: "m.emote", body: arg } },
      };

    case "topic":
      return { command: { action: "state", room_id: roomID, type: "m.room.topic", content: { topic: arg } } };

    case "nick":
      if (!arg) return { error: "/nick needs a display name" };
      return { membership: { displayname: arg } };

    case "away":
      return { membership: { status_msg: arg || "away" } };

    case "back":
      return { membership: { status_msg: "" } };

    case "slowmode": {
      const secs = arg === "" ? 0 : Number(arg);
      if (!Number.isFinite(secs) || secs < 0) return { error: "/slowmode <seconds> (0 to disable)" };
      return { slowmode: Math.floor(secs) };
    }

    case "poll": {
      const parts = arg.split("|").map((p) => p.trim()).filter(Boolean);
      if (parts.length < 3) return { error: "/poll Question | Option A | Option B [| …]" };
      const [question, ...answers] = parts;
      return { poll: { question, answers } };
    }

    case "join":
      if (!arg) return { error: "/join needs a room ID" };
      return { command: { action: "join", room_id: arg } };

    case "leave":
    case "part":
      // Leave the room you supply, or the current one.
      return { command: { action: "leave", room_id: arg || roomID } };

    case "knock":
      if (!arg) return { error: "/knock needs a room ID" };
      return { command: { action: "knock", room_id: arg } };

    case "invite":
      if (!arg) return { error: "/invite needs a user ID" };
      return { command: { action: "invite", room_id: roomID, state_key: arg } };

    case "kick":
    case "ban": {
      const [user] = rest;
      if (!user) return { error: `/${cmd} needs a user ID` };
      return { command: { action: cmd as "kick" | "ban", room_id: roomID, state_key: user } };
    }

    case "joinrule": {
      const allowed = ["public", "invite", "knock", "restricted"];
      if (!allowed.includes(arg)) return { error: `/joinrule expects one of ${allowed.join(", ")}` };
      return { command: { action: "state", room_id: roomID, type: "m.room.join_rules", state_key: "", content: { join_rule: arg } } };
    }

    default:
      return { error: `unknown command /${cmd} — try /help` };
  }
}
